import {DecimalPipe} from '@angular/common'
import {HttpErrorResponse} from '@angular/common/http'
import {Component, computed, inject, signal} from '@angular/core'
import {MatButton} from '@angular/material/button'
import {MatProgressBar} from '@angular/material/progress-bar'
import {Router} from '@angular/router'
import {IFileUpload} from '@sparbanken-syd/spb-file-mgmt'
import {IDocument} from '@sparbanken-syd/user-documents-backend'
import {catchError, concatMap, filter, map, NEVER, Observable, ReplaySubject, tap} from 'rxjs'
import {DOCUMENT_PATH} from '../../application/types'
import {SelectedComponent} from '../../components/selected/selected.component'
import {DocumentService} from '../../services/document.service'
import {ProgressIndicatorService} from '../../services/progress-indicator.service'
import {UploadSelectComponent} from './upload-select/upload-select.component'

@Component({
  selector: 'spb-upload-file',
  templateUrl: './upload-file.component.html',
  styleUrls: ['./upload-file.component.scss'],
  imports: [
    MatProgressBar,
    MatButton,
    SelectedComponent,
    UploadSelectComponent,
    DecimalPipe
  ]
})
export class UploadFileComponent {
  public total = signal<number>(0)
  public remaining = signal<number>(0)
  /**
   * Tracks upload progress, should always be a value 0 <> 100
   */
  public progress = computed(() => {
    if (this.total() === 0) {
      return 0
    }
    return (1 - this.remaining() / this.total()) * 100
  })

  public secondProgressBar$ = signal<boolean>(false)

  public documentService = inject(DocumentService)
  protected readonly progressService = inject(ProgressIndicatorService)

  private readonly router = inject(Router)

  private readonly queue = new ReplaySubject<IFileUpload>(1)

  constructor() {
    this.queue
      .pipe(
        tap((f) => {
          this.total.update(t => t + f.size)
          this.remaining.update(t => t + f.size)
        }),
        concatMap(v => this.sendDocument(v)),
        filter(() => this.remaining() === 0)
      )
      .subscribe(() => this.total.set(0))
  }

  public receiveDocument(file: IFileUpload): void {
    this.queue.next(file)
  }

  public sendDocument = (file: IFileUpload): Observable<any> => {
    let reported = 1
    // Send one byte to trigger the progressbar
    this.remaining.update(r => r - 1)
    return this.documentService.uploadDocumentData(file)
      .pipe(
        // Map of HttpEvents and handle them based on their type
        map(event => {
          if (event.status === 'progress') {
            // Here, we update 'progress' representing upload progress in percentage
            const sentBytes = event.message! - reported
            reported += sentBytes
            this.remaining.update(r => r - sentBytes)
          }
          return event
        }),
        // Filter out only events of type Response signaling that the upload is complete
        filter(event => event.status === 'completed'),
        catchError((e) => {
          // Reset progress and handle any errors that occur during the upload
          this.resetAfterError(e)
          return NEVER
        })
      )
  }

  public deleteFile(id: string): void {
    this.documentService.deleteDocument(id)
      .subscribe(() => {
        this.documentService.createdDocuments
          .update(f => f.filter((d: IDocument) => d.id !== id))
      })

  }

  public save() {
    this.secondProgressBar$.set(true)
    this.documentService.moveAndSaveDocuments()
      .pipe(
        catchError((e) => {
          this.resetAfterError(e)
          return NEVER
        }),
        tap(() => {
          // If we do this in a tap, instead of subscribe
          // We can let the service clean up the progress.
          this.router.navigate([DOCUMENT_PATH, {outlets: {item: 'klar'}}]).then()
        })
      )
      .subscribe()
  }

  private resetAfterError(e: HttpErrorResponse) {
    const errorMessage = e.error?.errorMessage || e.message
    this.progressService.progressRunning.set(false)
    this.remaining.set(0)
    // Pass the error message as a parameter to the next component
    this.router.navigate(
      [DOCUMENT_PATH, {outlets: {item: 'fel'}}],
      {state: {errorMessage}}).then()
  }
}
